import { ReactNode } from 'react';
import { useMfaStatus } from '@/hooks/useMfaStatus';
import MfaChallengeDialog from '@/components/MfaChallengeDialog';
import MfaSetupCard from '@/components/MfaSetupCard';
import { Loader2, ShieldAlert } from 'lucide-react';

interface Props {
  children: ReactNode;
}

/**
 * Hard gate for /admin/* routes.
 *
 * - AAL2 verified this session → render children normally.
 * - MFA enrolled but not yet verified this session → non-dismissible challenge dialog.
 * - No MFA enrolled → full-page blocking enrolment prompt.
 *
 * The user is assumed to already have super_admin role (enforced by ProtectedRoute).
 */
export default function AdminMfaGuard({ children }: Props) {
  const { isLoading, isEnrolled, isVerifiedThisSession, verifiedFactor, refresh } = useMfaStatus();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Checking security status…</p>
        </div>
      </div>
    );
  }

  // ── Fully verified this session — allow access ──────────────────────────
  if (isVerifiedThisSession) {
    return <>{children}</>;
  }

  // ── Enrolled but not yet challenged this session ─────────────────────────
  if (isEnrolled && verifiedFactor) {
    return (
      <>
        {/* Render children as inert backdrop so the page doesn't flash blank */}
        <div className="pointer-events-none opacity-0 select-none" aria-hidden>
          {children}
        </div>
        <MfaChallengeDialog
          factorId={verifiedFactor.id}
          open={true}
          title="Admin access requires MFA"
          description="Enter the 6-digit code from your authenticator app to continue."
          onSuccess={async () => { await refresh(); }}
          // No onCancel — cannot dismiss
        />
      </>
    );
  }

  // ── No MFA enrolled — blocking enrolment prompt ──────────────────────────
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-md w-full space-y-6">
        <div className="text-center space-y-2">
          <div className="mx-auto h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center">
            <ShieldAlert className="h-7 w-7 text-amber-600" />
          </div>
          <h1 className="text-xl font-semibold">MFA required for admin access</h1>
          <p className="text-sm text-muted-foreground">
            Super admin accounts must have two-factor authentication enabled before accessing the admin console.
            Set up your authenticator app below — it only takes a minute.
          </p>
        </div>
        <MfaSetupCard />
      </div>
    </div>
  );
}
